import * as React from 'react'

import { difference, uniq, xor } from 'lodash'

import { observer } from 'mobx-react'

export interface WithMultiSelectStateChildrenProps<T> {
  items: T[]
  selected: T[]
  onToggleSelectedItem: (item: T) => void
  add: (items: T[]) => void
  remove: (items: T[]) => void
  onToggleRange: (item: T) => void
  onSelectAll: () => void
  onDeselectAll: () => void
}
interface Props<T> {
  items: T[]
  initialSelection?: T[]
}

function WithMultiSelectState<T>({
  items,
  initialSelection,
  children,
}: Props<T> & React.ConsumerProps<WithMultiSelectStateChildrenProps<T>>) {
  const [selected, setSelected] = React.useState<T[]>(initialSelection || [])
  const [lastClickedItem, setLastClickedItem] = React.useState<T | null>(null)

  const onSelectAll = React.useCallback(() => {
    setSelected([...items])
  }, [items])

  const onDeselectAll = React.useCallback(() => {
    setSelected([])
  }, [])

  const add = React.useCallback(
    (itemsToAdd: T[]) => {
      setSelected((prevSelected) => uniq(prevSelected.concat(itemsToAdd)))
      setLastClickedItem(itemsToAdd[itemsToAdd.length - 1] ?? null)
    },
    []
  )

  const remove = React.useCallback((itemsToRemove: T[]) => {
    setSelected((prevSelected) => difference(prevSelected, itemsToRemove))
    setLastClickedItem(itemsToRemove[itemsToRemove.length - 1] ?? null)
  }, [])

  const onToggleRange = React.useCallback(
    (item: T) => {
      if (!lastClickedItem) return

      const lastClickedIndex = items.indexOf(lastClickedItem)
      const currentClickedIndex = items.indexOf(item)

      const [firstIndex, lastIndex] = [lastClickedIndex, currentClickedIndex].sort()

      const sliced = items.slice(firstIndex, lastIndex)
      setSelected((prevSelected) => uniq([...prevSelected, ...sliced, item]))
      setLastClickedItem(item)
    },
    [items, lastClickedItem]
  )

  const onToggleSelectedItem = React.useCallback(
    (item: T) => {
      setSelected((prevSelected) => xor(prevSelected, [item]))
      setLastClickedItem(item)
    },
    []
  )

  return children({
    items,
    selected,
    add,
    remove,
    onToggleSelectedItem,
    onToggleRange,
    onSelectAll,
    onDeselectAll,
  })
}

export default observer(WithMultiSelectState)
